// Comprehensive Findings Data for TMF Project
// Data extracted from tmf-findeings.pdf

// ==================== INTERFACES ====================

export interface UserType {
  id: number;
  title: string;
  description: string;
  themeRef: number;
  position: { x: number; y: number };
}

export interface Barrier {
  id: number;
  title: string;
  themes: number[];
  description: string;
  quotes: string[];
  color: 'yellow' | 'red' | 'purple';
}

export interface Need {
  title: string;
  items: string[];
  themeRef: number;
}

export interface Motivation {
  title: string;
  items: string[];
  themeRef: number;
}

export interface Aspiration {
  title: string;
  items: string[];
  themeRef: number;
}

export interface Gap {
  id: number;
  title: string;
  description: string;
}

export interface Recommendation {
  id: number;
  title: string;
  description: string;
  keyActions: string[];
  impactArea: string;
}

export interface AppreciationQuote {
  text: string;
  category: 'staff' | 'community' | 'efficiency' | 'impact' | 'services';
}

// ==================== USER TYPES DATA ====================

export const userTypes: UserType[] = [
  {
    id: 1,
    title: "Not Aware",
    description: "View pantries solely as food providers. Don't know non-food services exist",
    themeRef: 4,
    position: { x: 80, y: 200 }
  },
  {
    id: 2,
    title: "Interested in Learning More",
    description: "Awareness itself is primary barrier. Know something exists but lack details",
    themeRef: 4,
    position: { x: 200, y: 120 }
  },
  {
    id: 3,
    title: "Not Interested / No Perceived Need",
    description: "Reject education, financial guidance, job coaching. Specific Service Disinterest",
    themeRef: 4,
    position: { x: 200, y: 280 }
  },
  {
    id: 4,
    title: "Aware but Not Using",
    description: "Lack of Time - Working multiple jobs prevents engagement",
    themeRef: 4,
    position: { x: 340, y: 120 }
  },
  {
    id: 5,
    title: "Have Used Services",
    description: "Clothing, English classes, financial counseling. But no follow up",
    themeRef: 4,
    position: { x: 460, y: 120 }
  },
  {
    id: 6,
    title: "Need Clear Information / Pathway Uncertainty",
    description: "Information Quality Issues - Unclear, outdated, jargon heavy",
    themeRef: 4,
    position: { x: 340, y: 280 }
  }
];

// ==================== DEEPER BARRIERS DATA ====================

export const barriers: Barrier[] = [
  {
    id: 1,
    title: "Working Hungry",
    themes: [4, 9],
    description: "Employed but need pantry. Time scarcity prevents service engagement even when aware",
    quotes: [
      "Already works many jobs and does not have time",
      "Currently underemployed, working occasionally",
      "Working Hungry population - employed but need pantry"
    ],
    color: 'yellow'
  },
  {
    id: 2,
    title: "Stigma & Emotional Safety",
    themes: [4, 9],
    description: "Stigma prevents asking even when need identified",
    quotes: [
      "First Impression (experience) is important",
      "Does not like to ask for help (beyond food)",
      "Nervous about getting help with work or money",
      "'A lot' repeated to avoid sharing details"
    ],
    color: 'red'
  },
  {
    id: 3,
    title: "Language Barriers",
    themes: [9, 5],
    description: "Language barrier → Can't access information → Can't engage services",
    quotes: [
      "Language is a challenge in accessing help",
      "Most of Spanish speaking community in need of English learning assistance"
    ],
    color: 'purple'
  },
  {
    id: 4,
    title: "Unclear Pathways",
    themes: [4],
    description: "Awareness ≠ Access (knowing exists ≠ knowing how to start)",
    quotes: [
      "Don't have detailed information",
      "Lack clear descriptions, concrete next steps, accurate logistics",
      "Information Quality Gaps"
    ],
    color: 'yellow'
  },
  {
    id: 5,
    title: "Transportation & Mobility",
    themes: [3],
    description: "Even when aware, interested, and have time, physical access prevents participation",
    quotes: [
      "Difficulty coming to pantry because of not having gas all the time",
      "Can't carry groceries far",
      "Seniors with mobility issues are not able to get out for basic supplies",
      "Bad weather can be a barrier to pantry",
      "Reliable Transportation as need to continue services"
    ],
    color: 'yellow'
  }
];

// ==================== NEEDS / MOTIVATIONS / ASPIRATIONS DATA ====================

export const needs: Need[] = [
  {
    title: "Education",
    items: [
      "English literacy and education as path to stability",
      "Most of Spanish speaking community need English learning assistance"
    ],
    themeRef: 5
  },
  {
    title: "Employment & Income",
    items: [
      "Money is priority. Learning & education are secondary",
      "Secondary Job Source - Part-time work, alternative income streams"
    ],
    themeRef: 5
  },
  {
    title: "Low-Pressure Support",
    items: [
      "Warm, Low-Pressure Support Feels Safest",
      "Preferred roles: Helpful Neighbor, Caring Friend"
    ],
    themeRef: 5
  },
  {
    title: "Personal Interaction",
    items: [
      "Wrote their names, want to be identified personally",
      "Dedicated Consultation / Personal Interaction"
    ],
    themeRef: 5
  }
];

export const motivations: Motivation[] = [
  {
    title: "Family as Primary",
    items: [
      "Family is their motivation (multiple instances)",
      "Their Daughter is their motivation",
      "Loves being a dad (motivator)",
      "Motivated to improve for family"
    ],
    themeRef: 8
  },
  {
    title: "Faith",
    items: [
      "Faith, Gratitude & Personal Meaning",
      "Faith as strength during hardship"
    ],
    themeRef: 8
  },
  {
    title: "Hobbies",
    items: [
      "User has many hobbies: sculpting, drawing, blacksmithing, leather work and metalsmithing",
      "Fiance likes to bake as a hobby"
    ],
    themeRef: 8
  },
  {
    title: "Community",
    items: [
      "Community Belonging",
      "Usually in their motivated places or activities people tend to be in receiving mode"
    ],
    themeRef: 8
  }
];

export const aspirations: Aspiration[] = [
  {
    title: "Housing & Transportation",
    items: [
      "Having own house and car as a stable and happy self",
      "Hope to find a home for family"
    ],
    themeRef: 6
  },
  {
    title: "Financial Stability",
    items: [
      "Wants to be Financially Stable",
      "Income stability"
    ],
    themeRef: 6
  },
  {
    title: "Personal Growth",
    items: [
      "Want to improve themselves",
      "Having confidence to approach job because of having right education"
    ],
    themeRef: 6
  },
  {
    title: "Better Living for Children",
    items: [
      "Better future for their kids being the motivation to keep going when things feel tough",
      "Aspiring to work more so they can help their family (best self)"
    ],
    themeRef: 6
  },
  {
    title: "Giving Back",
    items: [
      "Community reciprocity: Help youngsters build businesses",
      "'Help when you can that's what the world needs'"
    ],
    themeRef: 6
  }
];

// ==================== PANTRY RECRUITMENT GAPS DATA ====================

export const gaps: Gap[] = [
  {
    id: 1,
    title: "Pantry Visit ≠ Service Recruitment Moment",
    description: "Pantry is functional place > not good recruitment place. Users rushed, focused on food, not receptive to additional asks"
  },
  {
    id: 2,
    title: "Information Alone Insufficient",
    description: "Informational strategies alone insufficient. Awareness ≠ Interest ≠ Use (non linear pathway)"
  },
  {
    id: 3,
    title: "Misalignment Between Services & Expressed Needs",
    description: "People need different help / some need some don't need. Pantry cannot help them with working toward their goal"
  }
];

// ==================== RECOMMENDATIONS DATA ====================

export const recommendations: Recommendation[] = [
  {
    id: 1,
    title: "Multi-Channel, Motivation-Based Outreach",
    description: "Reach people where they're emotionally open and engaged",
    keyActions: [
      "Leverage existing events (Family Fun Nights, School Readiness Events, Parent Cafés)",
      "Introduce services during events where families already engaged ('receiving mode')",
      "Multi-channel approaches to disseminate information (personalized)"
    ],
    impactArea: "Awareness & Trust Building"
  },
  {
    id: 2,
    title: "Trusted Intermediary Model",
    description: "Partner with institutions families already trust",
    keyActions: [
      "Schools and Family Networks as introduction points",
      "Pastor and Volunteer based suggestions for faith community partnerships",
      "Partner with schools and religious institutions for service promotion"
    ],
    impactArea: "Stigma Reduction & Community Trust"
  },
  {
    id: 3,
    title: "Peer Ambassador / Alumni Network",
    description: "Graduates become success story advocates",
    keyActions: [
      "Identify who went through the program > ambassador/alumni story sharing support back",
      "Recruit graduates as peer navigators sharing success stories",
      "Coming to the pantry for 2 years (loyalty despite gaps)"
    ],
    impactArea: "Social Proof & Peer Support"
  },
  {
    id: 4,
    title: "Language-Accessible Services",
    description: "Dedicated ESL pathway integrated with other services",
    keyActions: [
      "Most of Spanish speaking community in need of English learning assistance",
      "Language is a challenge in accessing help",
      "Bilingual staff/materials for all service touchpoints"
    ],
    impactArea: "Accessibility & Inclusion"
  },
  {
    id: 5,
    title: "First Impression Strategy",
    description: "Dedicated welcome orientation explaining full service range",
    keyActions: [
      "First Impression (experience) is important",
      "First visit experience went well",
      "Dedicated welcome orientation (not during food pickup)"
    ],
    impactArea: "Service Discovery & Engagement"
  },
  {
    id: 6,
    title: "Low-Pressure Consultation Model",
    description: "Gentle, personalized support without pressure",
    keyActions: [
      "Warm, Low Pressure Support Feels Safest",
      "Wrote their names, want to be identified personally",
      "Have sessions to at least note the pantry users that want to engage in service (First step)"
    ],
    impactArea: "Emotional Safety & Trust"
  },
  {
    id: 7,
    title: "Journey-Based Information Delivery",
    description: "Stage appropriate messaging matched to user journey phase",
    keyActions: [
      "User Journey Not Linear: Awareness ≠ Interest ≠ Use",
      "Stage appropriate messaging (initial visit vs. returning user)",
      "Information timing matters as much as content"
    ],
    impactArea: "Effective Communication & Engagement"
  }
];

// ==================== APPRECIATION QUOTES DATA ====================

export const appreciationQuotes: AppreciationQuote[] = [
  // Staff Excellence
  {
    text: "Pantry has good service and excellent staff",
    category: 'staff'
  },
  {
    text: "Neighbors mentioned SECS staff are very kind to everyone",
    category: 'staff'
  },
  {
    text: "They are welcoming and they help you with stuff",
    category: 'staff'
  },
  {
    text: "Describes support staff as kind and organized",
    category: 'staff'
  },

  // Community Belonging
  {
    text: "SECS is viewed as a community, not just a service",
    category: 'community'
  },
  {
    text: "Neighbors feel they are treated as another family united with community",
    category: 'community'
  },
  {
    text: "No discrimination from pantry towards neighbors",
    category: 'community'
  },

  // Operational Efficiency
  {
    text: "Set the account on Pantry soft is simple",
    category: 'efficiency'
  },
  {
    text: "Order on Pantry soft website is simple",
    category: 'efficiency'
  },
  {
    text: "It's because when you are right, your food is ready, you just pull up in the line with your car and they can help you to put it in your car. So it's easy",
    category: 'efficiency'
  },

  // Impact on Lives
  {
    text: "Pantry helps them a lot",
    category: 'impact'
  },
  {
    text: "Grateful for help from SECS",
    category: 'impact'
  },
  {
    text: "For me, all the service they have is very good. I am very satisfied with everything",
    category: 'impact'
  },
  {
    text: "Sees pantry as a 'blessing' as they are working hungry",
    category: 'impact'
  },

  // Successful Services
  {
    text: "Pantry helped them pay their rent",
    category: 'services'
  },
  {
    text: "The English teacher was very kind and patient",
    category: 'services'
  },
  {
    text: "English classes, rent assistance proving high impact when accessed",
    category: 'services'
  }
];

// ==================== HELPER FUNCTIONS ====================

export const getUserTypeById = (id: number): UserType | undefined => {
  return userTypes.find(type => type.id === id);
};

export const getBarrierById = (id: number): Barrier | undefined => {
  return barriers.find(barrier => barrier.id === id);
};

export const getRecommendationById = (id: number): Recommendation | undefined => {
  return recommendations.find(rec => rec.id === id);
};

export const getQuotesByCategory = (category: AppreciationQuote['category']): AppreciationQuote[] => {
  return appreciationQuotes.filter(quote => quote.category === category);
};

// ==================== CATEGORY LABELS ====================

export const APPRECIATION_CATEGORIES = {
  staff: 'Staff Excellence',
  community: 'Community Belonging',
  efficiency: 'Operational Efficiency',
  impact: 'Impact on Lives',
  services: 'Successful Services'
} as const;
